import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatNumber } from "../formatNumber";

const chartWidth = 880;
const chartHeight = 320;
const chartPadding = { top: 24, right: 24, bottom: 44, left: 92 };

const getYAxisStep = (maxValue) => {
  if (maxValue <= 10000) return 1000;
  if (maxValue <= 50000) return 5000;
  return 10000;
};

const ReportsPassiveIncomeTracking = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const reportRes = await apiClient.get("/periods");
        setRows(Array.isArray(reportRes.data) ? reportRes.data : []);
      } catch (err) {
        setError("Failed to load passive income tracking report.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const chartData = useMemo(() => {
    const points = [...rows]
      .sort((a, b) => {
        const left = String(a.DateEnd || "");
        const right = String(b.DateEnd || "");
        if (left !== right) return left.localeCompare(right);
        return Number(a.PeriodId || 0) - Number(b.PeriodId || 0);
      })
      .map((row) => ({
        label: row.Description || `Period ${row.PeriodId}`,
        value: Number(row.SavingsIncomeAmount || 0),
      }));

    while (points.length > 0 && points[points.length - 1].value === 0) {
      points.pop();
    }

    const rawMaxValue = points.reduce((max, point) => Math.max(max, point.value), 0);
    const yAxisStep = getYAxisStep(rawMaxValue);
    const maxValue = rawMaxValue > 0 ? Math.ceil(rawMaxValue / yAxisStep) * yAxisStep : yAxisStep;
    const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
    const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;
    const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;
    const scaleY = maxValue > 0 ? innerHeight / maxValue : 0;

    const linePoints = points.map((point, index) => ({
      x: chartPadding.left + stepX * index,
      y: chartHeight - chartPadding.bottom - point.value * scaleY,
      value: point.value,
    }));

    const linePath = linePoints
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    const tickValues = [];
    for (let tick = maxValue; tick >= 0; tick -= yAxisStep) {
      tickValues.push(tick);
    }

    return { points, linePoints, linePath, tickValues, maxValue, innerHeight };
  }, [rows]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Passive Income Tracking</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Passive Income Tracking
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading passive income tracking...</p>}

      {!loading && (
        <section className="panel">
          {chartData.points.length === 0 ? (
            <p className="notice notice-muted">No reporting period data found.</p>
          ) : (
            <>
              <div className="line-chart-legend">
                <span className="line-chart-legend-item">
                  <span className="line-chart-dot line-chart-dot-passive" /> Passive income
                </span>
              </div>

              <div className="table-scroll">
                <svg
                  className="line-chart"
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  role="img"
                  aria-label="Line chart showing passive income for each reporting period"
                >
                  {chartData.tickValues.map((tick) => {
                    const y =
                      chartHeight -
                      chartPadding.bottom -
                      (chartData.maxValue > 0 ? (tick / chartData.maxValue) * chartData.innerHeight : 0);
                    return (
                      <g key={tick}>
                        <line
                          className="line-chart-grid"
                          x1={chartPadding.left}
                          y1={y}
                          x2={chartWidth - chartPadding.right}
                          y2={y}
                        />
                        <text
                          className="line-chart-axis-text"
                          x={chartPadding.left - 10}
                          y={y + 4}
                          textAnchor="end"
                        >
                          {formatNumber(tick)}
                        </text>
                      </g>
                    );
                  })}

                  <line
                    className="line-chart-axis"
                    x1={chartPadding.left}
                    y1={chartPadding.top}
                    x2={chartPadding.left}
                    y2={chartHeight - chartPadding.bottom}
                  />
                  <line
                    className="line-chart-axis"
                    x1={chartPadding.left}
                    y1={chartHeight - chartPadding.bottom}
                    x2={chartWidth - chartPadding.right}
                    y2={chartHeight - chartPadding.bottom}
                  />

                  <path className="line-chart-path line-chart-path-passive" d={chartData.linePath} />

                  {chartData.linePoints.map((point, index) => (
                    <circle
                      key={`passive-${chartData.points[index].label}`}
                      className="line-chart-point line-chart-point-passive"
                      cx={point.x}
                      cy={point.y}
                      r="4"
                    />
                  ))}

                  {chartData.points.map((point, index) => {
                    const showLabel =
                      index === 0 ||
                      index === chartData.points.length - 1 ||
                      index % 5 === 0;
                    if (!showLabel) return null;
                    return (
                      <text
                        key={`label-${point.label}`}
                        className="line-chart-axis-text"
                        x={chartData.linePoints[index].x}
                        y={chartHeight - 16}
                        textAnchor="middle"
                      >
                        {point.label}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsPassiveIncomeTracking;
